import { ButtonDestructive, ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import { Licenses, AddOns } from "@nova/constants"
import { selectAllAddOns, selectAllLicenses } from "features/entity-caching/licenses/selectors"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useMemo, ReactNode } from "react"
import * as Icons from "@nova/component-icons"
import { LicenseBadge } from "@nova/ds-component-license-badge"
import { AddOnIcon } from "plugins/AddonIcon"
import classNames from "classnames"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { ProfilePhoto } from "../profile/ProfilePhoto"
import { selectAllRegisteredMembers } from "features/entity-caching/members/UserManagementSlice"
import { TriCheckbox } from "@nova/component-checkbox"
import {
    selectSelectedLicenseIds,
    selectAssignToMemberId,
    toggleSelectedLicenseId,
    setAssignToMemberId,
    setAssignToNonMemberEmail,
    selectAssignToNonMemberEmail,
} from "./slice"
import { ContactInputSingleSelect } from "themes/default-tailwind/buttons/ContactInput"

const useSubscriptionSelectionData = (currentAssigneeId: string | undefined) => {
    const allMembers = useAppSelector(selectAllRegisteredMembers)
    const member = useMemo(() => {
        if (!currentAssigneeId) return
        return allMembers.find((m) => m.userId === currentAssigneeId)
    }, [allMembers, currentAssigneeId])
    const allLicenses = useAppSelector(selectAllLicenses)
    const allAddons = useAppSelector(selectAllAddOns)
    const relevantLicenses = useMemo(
        () =>
            Object.values(allLicenses).filter((l) =>
                currentAssigneeId ? l.assigneeId === currentAssigneeId : !l.assigneeId
            ),
        [allLicenses, currentAssigneeId]
    )
    const relevantAddons = useMemo(
        () =>
            Object.values(allAddons).filter((a) =>
                currentAssigneeId ? a.assigneeId === currentAssigneeId : !a.assigneeId
            ),
        [allAddons, currentAssigneeId]
    )
    const licenses = useMemo(() => {
        return relevantLicenses.reduce(
            (acc, cur) => {
                const type = cur.subscriptionType as Licenses.Pro | Licenses.Essentials
                if (acc[type]) {
                    acc[type].count += 1
                    acc[type].oneId = cur.licenseId
                }
                return acc
            },
            {
                [Licenses.Pro]: { count: 0, oneId: "" },
                [Licenses.Essentials]: { count: 0, oneId: "" },
            }
        )
    }, [relevantLicenses])
    const hasAtLeastOneLicense =
        !!licenses[Licenses.Pro].count || !!licenses[Licenses.Essentials].count
    const addons = useMemo(() => {
        return relevantAddons.reduce(
            (acc, cur) => {
                const type = cur.subscriptionType as AddOns
                if (acc[type]) {
                    acc[type].count += 1
                    acc[type].oneId = cur.licenseId
                }
                return acc
            },
            {
                [AddOns.Hotspots]: { count: 0, oneId: "" },
                [AddOns.Colorfinder]: { count: 0, oneId: "" },
                [AddOns.LiveVideo]: { count: 0, oneId: "" },
            }
        )
    }, [relevantAddons])
    const hasAtLeastOneAddOn =
        !!addons[AddOns.Hotspots].count ||
        !!addons[AddOns.Colorfinder].count ||
        !!addons[AddOns.LiveVideo].count
    return { member, licenses, hasAtLeastOneLicense, addons, hasAtLeastOneAddOn }
}

const SubscriptionSelectionSection = ({
    label,
    currentAssigneeId,
}: {
    label: string
    currentAssigneeId: string | undefined
}) => {
    const { member, licenses, hasAtLeastOneLicense, addons, hasAtLeastOneAddOn } =
        useSubscriptionSelectionData(currentAssigneeId)
    const dispatch = useAppDispatch()
    const selectedLicenseIds = useAppSelector(selectSelectedLicenseIds)
    return (
        <div className="flex gap-6">
            <div className="w-[224px]">{label}</div>
            <div className="flex flex-col gap-4">
                {member && (
                    <div className="flex items-center gap-2">
                        <div className="w-8 min-w-[32px] aspect-square rounded-lg overflow-hidden">
                            <ProfilePhoto userId={member.userId} fullName={member.fullName} />
                        </div>
                        <div className="flex flex-col">
                            <div>{member.fullName}</div>
                            <small className="text-gray-200">{member.email}</small>
                        </div>
                    </div>
                )}
                {hasAtLeastOneLicense && (
                    <div className="flex flex-col gap-1">
                        <small className="xs">LICENSES</small>
                        {Object.entries(licenses).map(([key, val]) =>
                            !!val.count ? (
                                <SubscriptionRow
                                    key={key}
                                    iconElement={<LicenseBadge license={key as Licenses} />}
                                    title={`${key[0].toUpperCase()}${key.slice(1)} License`}
                                    count={val.count}
                                    isSelected={selectedLicenseIds.includes(val.oneId)}
                                    select={() => dispatch(toggleSelectedLicenseId(val.oneId))}
                                />
                            ) : undefined
                        )}
                    </div>
                )}
                {hasAtLeastOneAddOn && (
                    <div className="flex flex-col gap-1">
                        <small className="xs">ADD-ONS</small>
                        {Object.entries(addons).map(([key, val]) =>
                            !!val.count ? (
                                <SubscriptionRow
                                    key={key}
                                    iconElement={<AddOnIcon addOn={key as AddOns} />}
                                    title={
                                        key === "liveVideo"
                                            ? "Live Stream"
                                            : `${key[0].toUpperCase()}${key.slice(1)}`
                                    }
                                    count={val.count}
                                    isSelected={selectedLicenseIds.includes(val.oneId)}
                                    select={() => dispatch(toggleSelectedLicenseId(val.oneId))}
                                />
                            ) : undefined
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export const AssignSubscriptionsModal = ({
    confirm,
    cancel,
    isPerformingAsyncConfirmCallback,
    currentAssigneeId,
}: {
    confirm: () => void
    cancel: () => void
    isPerformingAsyncConfirmCallback: boolean
    currentAssigneeId: string | undefined
}) => {
    const dispatch = useAppDispatch()
    const selectedLicenseIds = useAppSelector(selectSelectedLicenseIds)
    const assignToMemberId = useAppSelector(selectAssignToMemberId)
    const assignToNonMemberEmail = useAppSelector(selectAssignToNonMemberEmail)
    const allMembers = useAppSelector(selectAllRegisteredMembers)
    const assignToMember = useMemo(
        () => allMembers.find((m) => m.userId === assignToMemberId),
        [allMembers, assignToMemberId]
    )
    const hasSelectedValidRecipient =
        (assignToMember && assignToMember.userId !== currentAssigneeId) ||
        (assignToNonMemberEmail && assignToNonMemberEmail !== currentAssigneeId)
    return (
        <HighPriorityNovaModalContainer
            title={!!currentAssigneeId ? "Re-assign a subscription" : "Assign a subscription"}
            bottomBar={
                <div className="w-full flex justify-end items-center gap-2">
                    <ButtonTertiary title="Cancel" onClick={cancel} />
                    <ButtonPrimary
                        title="Confirm"
                        onClick={confirm}
                        RightIcon={Icons.Checkmark}
                        disabled={
                            !selectedLicenseIds.length ||
                            (!currentAssigneeId && !hasSelectedValidRecipient) ||
                            isPerformingAsyncConfirmCallback
                        }
                    />
                </div>
            }
            closeModal={cancel}
        >
            <div className="flex flex-col gap-6 pt-2.5">
                <SubscriptionSelectionSection
                    label={
                        !!currentAssigneeId
                            ? "Remove a subscription from:"
                            : "Subscription to assign:"
                    }
                    currentAssigneeId={currentAssigneeId}
                />
                <div className="w-full h-px bg-gray-700" />
                <div className="flex gap-6">
                    <div className="w-[224px]">Give the subscription to:</div>
                    <div className="grow">
                        {assignToMember ? (
                            <div className="flex items-center gap-2">
                                <div className="flex gap-1">
                                    <div className="w-8 min-w-[32px] aspect-square rounded-lg overflow-hidden">
                                        <ProfilePhoto
                                            userId={assignToMember.userId}
                                            fullName={assignToMember.fullName}
                                        />
                                    </div>
                                    <div className="flex flex-col">
                                        <div>{assignToMember.fullName}</div>
                                        <small className="text-gray-200">
                                            {assignToMember.email}
                                        </small>
                                    </div>
                                </div>
                                <div className="grow" />
                                <Icons.Icon
                                    Icon={Icons.Close}
                                    className="text-gray-300 hover:text-white transition-colors cursor-pointer"
                                    onClick={() => dispatch(setAssignToMemberId(""))}
                                />
                            </div>
                        ) : assignToNonMemberEmail ? (
                            <div className="flex items-center gap-2">
                                <div className="flex gap-1">
                                    <div className="w-8 min-w-[32px] aspect-square rounded-lg overflow-hidden">
                                        <ProfilePhoto
                                            userId={""}
                                            fullName={assignToNonMemberEmail}
                                        />
                                    </div>
                                    <div className="flex flex-col justify-center items-center">
                                        {assignToNonMemberEmail}
                                    </div>
                                </div>
                                <div className="grow" />
                                <Icons.Icon
                                    Icon={Icons.Close}
                                    className="text-gray-300 hover:text-white transition-colors cursor-pointer"
                                    onClick={() => dispatch(setAssignToNonMemberEmail(""))}
                                />
                            </div>
                        ) : (
                            <ContactInputSingleSelect
                                enabledFeatures={["email"]}
                                onSelect={(c) => {
                                    if (c.kind === "member") {
                                        dispatch(setAssignToMemberId(c.member.id))
                                    }
                                    if (c.kind === "savedContactEmail") {
                                        dispatch(setAssignToNonMemberEmail(c.contact.email!))
                                    }
                                    if (c.kind === "rawEmail") {
                                        dispatch(setAssignToNonMemberEmail(c.value))
                                    }
                                }}
                                placeholder="Search People"
                                leftItem={<Icons.Icon Icon={Icons.Plus} />}
                            />
                        )}
                    </div>
                </div>
            </div>
        </HighPriorityNovaModalContainer>
    )
}

export const UnassignSubscriptionsModal = ({
    confirm,
    cancel,
    isPerformingAsyncConfirmCallback,
    currentAssigneeId,
}: {
    confirm: () => void
    cancel: () => void
    isPerformingAsyncConfirmCallback: boolean
    currentAssigneeId: string
}) => {
    const selectedLicenseIds = useAppSelector(selectSelectedLicenseIds)
    return (
        <HighPriorityNovaModalContainer
            title="Unassign a subscription"
            bottomBar={
                <div className="w-full flex justify-end items-center gap-2">
                    <ButtonTertiary title="Cancel" onClick={cancel} />
                    <ButtonDestructive
                        title="Unassign"
                        onClick={confirm}
                        RightIcon={Icons.PersonRemove}
                        disabled={!selectedLicenseIds.length || isPerformingAsyncConfirmCallback}
                    />
                </div>
            }
            closeModal={cancel}
        >
            <div className="flex flex-col gap-6 pt-2.5">
                <SubscriptionSelectionSection
                    label="Remove a subscription from:"
                    currentAssigneeId={currentAssigneeId}
                />
            </div>
        </HighPriorityNovaModalContainer>
    )
}

const SubscriptionRow = ({
    iconElement,
    title,
    count,
    isSelected,
    select,
}: {
    iconElement: ReactNode
    title: string
    count: number
    isSelected: boolean
    select: () => void
}) => {
    return (
        <div
            className={classNames(
                "p-3 flex items-center gap-3 w-[364px] border rounded-lg cursor-pointer transition-colors",
                {
                    "border-gray-700 hover:border-white-300": !isSelected,
                    "border-white-300 bg-gray-700": isSelected,
                }
            )}
            onClick={select}
        >
            <TriCheckbox state={isSelected ? "checked" : "unchecked"} />
            {iconElement}
            <div className="grow">{title}</div>
            <div className="text-gray-300">{count} Available</div>
        </div>
    )
}
